import React, { useEffect, useState, useReducer } from "react";
import { Button, Col, Card, Container, Form, Row } from "react-bootstrap";
import {
  WarningCard,
  CardComponent,
  MessageToast,
  InputEdit,
} from "components";
import { useGnssApi, useToast, useAuth } from "utils/hooks";

import {
  validateCoord,
  validatePort,
  validateHostname,
} from "utils/helpers/validators";

import { stationDetailsReducer } from "utils/reducers/reducers";
import { buildStationDetailsPayload } from "utils/helpers/formatters";

const formRowStyle = {
  flexDirection: "row",
  alignContent: "flex-start",
  display: "flex",
  justifyContent: "flex-start",
  gap: "1rem",
};

export const GNSSAddStation = () => {
  const api = useGnssApi();
  const { openToast } = useToast();
  const { tenant_access_token, getAuthorizationByRoles } = useAuth();

  const [errorMessages, setErrorMessage] = useState({
    name: null,
    identifier: null,
    sisHostname: null,
    sisPort: null,
    intNorthHostname: null,
    intNorthPort: null,
    intEastHostname: null,
    intEastPort: null,
    intSouthHostname: null,
    intSouthPort: null,
    intWestHostname: null,
    intWestPort: null,
    longitude: null,
    latitude: null,
    elevation: null,
  });

  const availableGroups = getAuthorizationByRoles(["Administrator"]);

  const createInitialState = (availableGroups) => ({
    name: "",
    identifier: "",
    type: "PERFORMANCE",
    isSelfHosted: false,
    groupNames:
      availableGroups.length > 0 ? [availableGroups[0].groupName] : [],

    sis: { hostname: "", port: null },

    int: {
      north: { hostname: "", port: null },
      east: { hostname: "", port: null },
      south: { hostname: "", port: null },
      west: { hostname: "", port: null },
    },

    location: { lat: 0, lon: 0, elv: 0 },
  });

  const [newDetails, dispatch] = useReducer(
    stationDetailsReducer,
    availableGroups,
    createInitialState,
  );

  const addStationDetails = async (payload) => {
    const finalPayload = buildStationDetailsPayload(payload);

    const requiredFields = ["name", "identifier", "type", "groupNames"];
    const locationValid =
      finalPayload.location.lat &&
      finalPayload.location.lon &&
      finalPayload.location.elv;

    if (!requiredFields.every((f) => finalPayload[f]) || !locationValid) {
      openToast({
        message: `All mandatory fields are required`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
      return;
    }

    try {
      const response = await api.addStationDetails(finalPayload);
      if (["CONFLICT", "NOT_FOUND"].includes(response.status)) {
        openToast({
          message: `${response.message}`,
          type: "danger",
          autohide: true,
          delay: 5000,
        });
        return;
      }

      openToast({
        message: `${response.message}`,
        type: "success",
        autohide: true,
        delay: 5000,
      });

      return response;
    } catch (e) {
      openToast({
        message: `Station ${payload.name} (${payload.identifier}) cannot be created`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
    }
  };

  if (!availableGroups || availableGroups.length === 0)
    return (
      <WarningCard
        title={"No networks available"}
        message={
          <span>
            Your company doesn't have any networks. In order to add a station
            you need to create a new network. To add a new one{" "}
            <a href="/settings/networks" className="text-decoration-underline ">
              click here
            </a>
          </span>
        }
      />
    );

  return (
    <>
      <Row className="m-3">
        <CardComponent title="Add new GNSS Station">
          <Row>
            <Col
              xs={{ span: 12, offset: 0 }}
              sm={{ span: 12, offset: 0 }}
              md={{ span: 12, offset: 0 }}
              lg={{ span: 12, offset: 0 }}
              xl={{ span: 12, offset: 0 }}
              xxl={{ span: 6, offset: 0 }}
            >
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group
                  as={Col}
                  className="mb-1"
                  controlId="formStationName"
                >
                  <InputEdit
                    label="Station name*"
                    placeholder="The name of GNSS monitoring station"
                    error={errorMessages.name}
                    validationPattern={/[^a-zA-Z0-9 -]/g}
                    onFocus={() =>
                      setErrorMessage({ ...errorMessages, name: null })
                    }
                    onBlur={(e) => {
                      e.target.value.trim() === "" &&
                        setErrorMessage({
                          ...errorMessages,
                          name: "Field cannot be empty",
                        });
                    }}
                    onChange={(e) => {
                      dispatch({
                        type: "SET_FIELD",
                        field: "name",
                        value: e.target.value,
                      });
                    }}
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="groupName"
                  className="mt-3"
                  style={formRowStyle}
                >
                  <Form.Label>Networks* :</Form.Label>
                  {tenant_access_token &&
                    availableGroups.map((group, index) => (
                      <Form.Check
                        id={`${group.groupName + "-" + index}`}
                        key={group.groupName}
                        value={group.groupName}
                        label={group.groupName}
                        onChange={(e) => {
                          dispatch({
                            type: "TOGGLE_GROUP",
                            group: group.groupName,
                          });
                        }}
                        checked={newDetails.groupNames?.includes(
                          group.groupName,
                        )}
                      />
                    ))}
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="stationType"
                  className="mt-3"
                  style={formRowStyle}
                >
                  <Form.Label>Choose station type* :</Form.Label>
                  <Form.Check
                    id="stationType-1"
                    value="PERFORMANCE"
                    type="radio"
                    label="Performance"
                    checked={newDetails.type === "PERFORMANCE"}
                    onChange={() =>
                      dispatch({
                        type: "SET_FIELD",
                        field: "type",
                        value: "PERFORMANCE",
                      })
                    }
                  />
                  <Form.Check
                    id="stationType-2"
                    value="LOCALIZATION"
                    type="radio"
                    label="Localization"
                    checked={newDetails.type === "LOCALIZATION"}
                    onChange={() =>
                      dispatch({
                        type: "SET_FIELD",
                        field: "type",
                        value: "LOCALIZATION",
                      })
                    }
                  />
                  <Form.Check
                    id="stationType-3"
                    value="PERFORMANCE_AND_LOCALIZATION"
                    type="radio"
                    label="Performance and localization"
                    checked={newDetails.type === "PERFORMANCE_AND_LOCALIZATION"}
                    onChange={() =>
                      dispatch({
                        type: "SET_FIELD",
                        field: "type",
                        value: "PERFORMANCE_AND_LOCALIZATION",
                      })
                    }
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="selfHosted"
                  className="mt-3"
                  style={formRowStyle}
                >
                  <Form.Label>Self-hosted* :</Form.Label>
                  <Form.Check
                    id="stationType-1"
                    value="PERFORMANCE"
                    type="switch"
                    checked={newDetails.isSelfHosted}
                    onChange={() =>
                      dispatch({
                        type: "SET_FIELD",
                        field: "isSelfHosted",
                        value: !newDetails.isSelfHosted,
                      })
                    }
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formStationIdentifier"
                  className="mb-1"
                >
                  <InputEdit
                    label="Station ID*"
                    placeholder="4-character ID"
                    error={errorMessages.identifier}
                    validationPattern={/[^a-zA-Z0-9]/g}
                    onFocus={(e) => {
                      setErrorMessage({ ...errorMessages, identifier: null });
                    }}
                    onBlur={(e) =>
                      e.target.value.trim() === "" &&
                      setErrorMessage({
                        ...errorMessages,
                        identifier: "Field cannot be empty",
                      })
                    }
                    onChange={(e) =>
                      dispatch({
                        type: "SET_FIELD",
                        field: "identifier",
                        value: e.target.value.trim(),
                      })
                    }
                  />
                </Form.Group>

                <Form.Group as={Row} controlId="locationFields">
                  {["latitude", "longitude", "elevation"].map((coord) => (
                    <Form.Group
                      as={Col}
                      controlId={`form-${coord}`}
                      key={coord}
                    >
                      <InputEdit
                        label={`${
                          coord.charAt(0).toUpperCase() + coord.slice(1)
                        }*`}
                        placeholder={
                          coord === "elevation"
                            ? "Meters"
                            : "DD (Decimal Degrees)"
                        }
                        error={errorMessages[coord]}
                        onFocus={() =>
                          setErrorMessage({ ...errorMessages, [coord]: null })
                        }
                        onBlur={(e) =>
                          setErrorMessage({
                            ...errorMessages,
                            [coord]: validateCoord(e.target.value, coord),
                          })
                        }
                        onChange={(e) =>
                          dispatch({
                            type: "SET_LOCATION",
                            field:
                              coord === "longitude"
                                ? "lon"
                                : coord === "latitude"
                                  ? "lat"
                                  : "elv",
                            value: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  ))}
                </Form.Group>

                {["PERFORMANCE", "PERFORMANCE_AND_LOCALIZATION"].includes(
                  newDetails.type,
                ) &&
                  newDetails.isSelfHosted && (
                    <Form.Group as={Row} controlId="formSISConnParams">
                      <Form.Group as={Col} controlId="formSISHostname">
                        <InputEdit
                          label="Performance receiver hostname"
                          placeholder="Hostname/IP address"
                          cleanWhitespaces={true}
                          validationPattern={{}}
                          error={errorMessages.sisHostname}
                          onFocus={() =>
                            setErrorMessage({
                              ...errorMessages,
                              sisHostname: null,
                            })
                          }
                          onBlur={(e) => {
                            if (
                              !validateHostname(e.target.value) &&
                              e.target.value
                            )
                              setErrorMessage({
                                ...errorMessages,
                                sisHostname: "Invalid format",
                              });
                          }}
                          onChange={(e) =>
                            dispatch({
                              type: "SET_SIS",
                              field: "hostname",
                              value: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                      <Form.Group as={Col} md={4} controlId="formSISPort">
                        <InputEdit
                          label="Port"
                          placeholder="Default 80"
                          type="number"
                          error={errorMessages.sisPort}
                          onFocus={() =>
                            setErrorMessage({ ...errorMessages, sisPort: null })
                          }
                          onBlur={(e) => {
                            const port = validatePort(e.target.value);
                            if (port === null)
                              setErrorMessage({
                                ...errorMessages,
                                sisPort: "Invalid or out of range",
                              });
                          }}
                          onChange={(e) =>
                            dispatch({
                              type: "SET_SIS",
                              field: "port",
                              value: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Form.Group>
                  )}

                {["LOCALIZATION", "PERFORMANCE_AND_LOCALIZATION"].includes(
                  newDetails.type,
                ) &&
                  newDetails.isSelfHosted && (
                    <>
                      <Form.Group as={Row} controlId="formINTNorthConnParams">
                        <Form.Group as={Col} controlId="formINTNorthHostname">
                          <InputEdit
                            label="Localization receiver hostname (North)"
                            placeholder="Hostname/IP address of North oriented receiver"
                            cleanWhitespaces={true}
                            validationPattern={{}}
                            error={errorMessages.intHostname}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intHostname: null,
                              })
                            }
                            onBlur={(e) => {
                              if (
                                !validateHostname(e.target.value) &&
                                e.target.value
                              )
                                setErrorMessage({
                                  ...errorMessages,
                                  intHostname: "Invalid format",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "north",
                                field: "hostname",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          md={4}
                          controlId="formINTNorthPort"
                        >
                          <InputEdit
                            label="Port (North)"
                            placeholder="Default 80"
                            type="number"
                            error={errorMessages.intNorthPort}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intPort: null,
                              })
                            }
                            onBlur={(e) => {
                              const port = validatePort(e.target.value);
                              if (port === null)
                                setErrorMessage({
                                  ...errorMessages,
                                  intNorthPort: "Invalid or out of range",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "north",
                                field: "port",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Form.Group>
                      <Form.Group as={Row} controlId="formINTEastConnParams">
                        <Form.Group as={Col} controlId="formINTEastHostname">
                          <InputEdit
                            label="Localization receiver hostname (East)"
                            placeholder="Hostname/IP address of East oriented receiver"
                            cleanWhitespaces={true}
                            validationPattern={{}}
                            error={errorMessages.intEastHostname}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intEastHostname: null,
                              })
                            }
                            onBlur={(e) => {
                              if (
                                !validateHostname(e.target.value) &&
                                e.target.value
                              )
                                setErrorMessage({
                                  ...errorMessages,
                                  intEastHostname: "Invalid format",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "east",
                                field: "hostname",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={4} controlId="formINTEastPort">
                          <InputEdit
                            label="Port (East)"
                            placeholder="Default 80"
                            type="number"
                            error={errorMessages.intEastPort}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intEastPort: null,
                              })
                            }
                            onBlur={(e) => {
                              const port = validatePort(e.target.value);
                              if (port === null)
                                setErrorMessage({
                                  ...errorMessages,
                                  intEastPort: "Invalid or out of range",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "east",
                                field: "port",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Form.Group>
                      <Form.Group as={Row} controlId="formINTSouthConnParams">
                        <Form.Group as={Col} controlId="formINTSouthHostname">
                          <InputEdit
                            label="Localization receiver hostname (South)"
                            placeholder="Hostname/IP address of South oriented receiver"
                            cleanWhitespaces={true}
                            validationPattern={{}}
                            error={errorMessages.intSouthHostname}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intSouthHostname: null,
                              })
                            }
                            onBlur={(e) => {
                              if (
                                !validateHostname(e.target.value) &&
                                e.target.value
                              )
                                setErrorMessage({
                                  ...errorMessages,
                                  intSouthHostname: "Invalid format",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "south",
                                field: "hostname",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          md={4}
                          controlId="formINTSouthPort"
                        >
                          <InputEdit
                            label="Port (South)"
                            placeholder="Default 80"
                            type="number"
                            error={errorMessages.intPort}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intSouthPort: null,
                              })
                            }
                            onBlur={(e) => {
                              const port = validatePort(e.target.value);
                              if (port === null)
                                setErrorMessage({
                                  ...errorMessages,
                                  intSouthPort: "Invalid or out of range",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "south",
                                field: "port",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Form.Group>
                      <Form.Group as={Row} controlId="formINTWestConnParams">
                        <Form.Group as={Col} controlId="formINTWestHostname">
                          <InputEdit
                            label="Localization receiver hostname (West)"
                            placeholder="Hostname/IP address of West oriented receiver"
                            cleanWhitespaces={true}
                            validationPattern={{}}
                            error={errorMessages.intWestHostname}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intWestHostname: null,
                              })
                            }
                            onBlur={(e) => {
                              if (
                                !validateHostname(e.target.value) &&
                                e.target.value
                              )
                                setErrorMessage({
                                  ...errorMessages,
                                  intWestHostname: "Invalid format",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "west",
                                field: "hostname",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={4} controlId="formINTWestPort">
                          <InputEdit
                            label="Port (West)"
                            placeholder="Default 80"
                            type="number"
                            error={errorMessages.intWestPort}
                            onFocus={() =>
                              setErrorMessage({
                                ...errorMessages,
                                intWestPort: null,
                              })
                            }
                            onBlur={(e) => {
                              const port = validatePort(e.target.value);
                              if (port === null)
                                setErrorMessage({
                                  ...errorMessages,
                                  intWestPort: "Invalid or out of range",
                                });
                            }}
                            onChange={(e) =>
                              dispatch({
                                type: "SET_INT",
                                direction: "west",
                                field: "port",
                                value: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Form.Group>
                    </>
                  )}

                <Form.Group as={Row} className="mb-3">
                  <Col>
                    <p className="text-warning">All (*) fields are mandatory</p>
                  </Col>
                  <Col className="text-end">
                    <Button
                      variant="success"
                      disabled={Object.values(errorMessages).some(
                        (value) => value !== null,
                      )}
                      onClick={() => addStationDetails(newDetails)}
                    >
                      Add new station
                    </Button>
                  </Col>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </CardComponent>
      </Row>
    </>
  );
};
